import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";

import { useRoutesByUuids, useStripRoutesMutation } from "../../hooks/routes";
import { useCurrentVenueQuery } from "../../hooks/venues";
import { DateInput } from "../forms/TextInput";
import { RouteName } from "../RoutesTable/RouteName";
import { useModal } from "./useModal";

export const StripRoutes = ({
  routeUuids,
  onComplete,
}: {
  routeUuids: string[];
  onComplete?: () => void;
}) => {
  const { close } = useModal();

  const routes = useRoutesByUuids(routeUuids);
  const { data: venue } = useCurrentVenueQuery();

  const { formState, ...methods } = useForm({
    defaultValues: {
      endTime: "NOW",
    },
  });

  const mutation = useStripRoutesMutation();

  const onSubmit = async ({ endTime }: { endTime: string }) => {
    await mutation.mutateAsync({
      routeUuids,
      endTime,
    });
    onComplete?.();
    close();
  };

  const endTime = methods.watch("endTime");

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>
        {routes.length === 1 ? (
          <>
            Strip route:{" "}
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <RouteName route={routes[0]} />
            </Box>
          </>
        ) : (
          <>Strip {routes.length} routes</>
        )}
      </DialogTitle>
      <FormProvider formState={formState} {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                justifyContent: "center",
              }}
            >
              Strip route{routes.length !== 1 ? "s" : ""}
              <Select
                name="type"
                value={endTime === "NOW" ? "immediate" : "scheduled"}
                onChange={(_e, value) =>
                  methods.setValue(
                    "endTime",
                    value === "immediate" ? "NOW" : new Date().toISOString(),
                  )
                }
              >
                <Option value="immediate">Immediately</Option>
                <Option value="scheduled" disabled>
                  Later...
                </Option>
              </Select>
            </Box>
            {endTime !== "NOW" && (
              <Box sx={{ marginTop: 1 }}>
                <DateInput
                  label="Scheduled strip date"
                  name="endTime"
                  withTime={false}
                  defaultTime="12:00:00"
                  timezone={venue?.timezone}
                />
              </Box>
            )}
            <Typography color="danger" fontWeight="bold" sx={{ marginTop: 1 }}>
              Are you sure? This action can’t be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ flex: 1 }}
              type="submit"
              loading={formState.isSubmitting}
              loadingPosition="start"
              color="danger"
            >
              Strip
            </Button>
            <Button sx={{ flex: 1 }} onClick={close} color="neutral" variant="soft">
              Cancel
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </ModalDialog>
  );
};
